import { useState, useContext, useEffect } from 'react';
import gql from 'graphql-tag';
import _ from 'lodash';

import MercuryContext from 'ConnectionManager/mercuryContext';
import AccountHelper from 'Workspace/helpers/AccountHelper';

const queryDeploymentDocumentItems = gql`
query getDeploymentDocumentItems(
  $cohortId: UUID!,
  $deploymentId: UUID!,
  $isWorkspace: Boolean!
) {
  cohortById(cohortId: $cohortId) {
    id
    deployment(deploymentId: $deploymentId, isWorkspace: $isWorkspace) {
      activity {
        id
        elementType
        config
        linkedDocumentItems {
          edges {
            node {
              documentId
              id
              fullStatement
              abbreviatedStatement
            }
          }
        }
      }
    }
    enrollments {
      edges {
        node {
          account {
            id
            identity {
              email
              familyName
              givenName
              honorificSuffix
              avatars(size:SMALL) {
                data
              }
            }
          }
        }
      }
    }
  }
}
`;

/**
 * Fetch the enrollments for a cohort, and any documentItems linked to the given deployment.
 * @param {string} cohortId The cohort id
 * @param {string} deploymentId The deployment id
 * @returns  {{ loading: boolean, error: Object, data: Object }}
 * An apollo state object containing loading, error, and the fetched data state
 * (A list of enrollments, and a list of linkedDocumentItems).
 */
export default function useEnrollmentsAndLinkedItems(cohortId, deploymentId) {
  const [state, setState] = useState({ data: {} });
  const { apolloClient } = useContext(MercuryContext);

  useEffect(() => {
    setState({
      loading: true,
      error: null,
      data: {},
    });

    apolloClient.query({
      query: queryDeploymentDocumentItems,
      fetchPolicy: 'cache-first',
      variables: {
        cohortId,
        deploymentId,
        isWorkspace: true,
      },
    })
      .then((response) => {
        setState({
          loading: false,
          error: null,
          data: {
            enrollments: _.chain(response)
              .get('data.cohortById.enrollments.edges')
              .map(({ node }) => node)
              .sort((a, b) => AccountHelper.sortListByFamilyName(a.account.identity, b.account.identity))
              .value(),
            linkedDocumentItems: _.chain(response)
              .get('data.cohortById.deployment.0.activity.linkedDocumentItems.edges')
              .map(({ node }) => node)
              .value(),
          },
        });
      })
      .catch((error) => {
        setState({
          loading: false,
          error,
          data: {},
        });
      });
  }, [apolloClient, cohortId, deploymentId]);

  return state;
}
